/**
 * Per-frame beam angles. Snapshots arrive at 10 Hz, so drawing a beam at
 * the snapshot angle makes a sweep step visibly. Instead each beam keeps a
 * smoothed angle that turns every frame:
 *  - the local player's beam turns toward the local mouse aim at the same
 *    turn rate the server uses, so it tracks the cursor without waiting for
 *    a round trip (and is nudged back if the server disagrees, e.g. lock-on);
 *  - other players' beams chase the latest server angle at twice the turn
 *    rate, which turns 10 Hz steps into continuous motion.
 * Origins follow the owner's smoothly drawn position, so beams stay attached.
 * A beam bouncing off a crossed beam glides its crossing point the same way.
 */

import { BEAM, BEAM_TURN_RATE, STATUS_EFFECT, angleDiff, fromAngle, wrapAngle } from '@shared/index.js';
import type { BeamBounce, Vec2 } from '@shared/index.js';
import type { ViewBeam, ViewPlayer } from './viewModel.js';

const DEG = Math.PI / 180;
/** If the server angle is this far from ours, it wins faster (lock-on, cast lock, lag spike). */
const DISAGREE_RAD = 25 * DEG;

export interface LocalAim { pid: number; aim: number; turnMul: number }

/** How fast a drawn bounce catches up with the server's (per second): 10 Hz steps become a glide. */
const BOUNCE_FOLLOW = 22;

export class BeamSmoother {
  private angles = new Map<number, number>();
  private bounces = new Map<number, BeamBounce>();

  /** Rewrites `beams` in place with smoothed angles and owner-attached origins, and glides each bounce. */
  apply(beams: ViewBeam[], players: readonly ViewPlayer[], local: LocalAim | null, dt: number): void {
    const live = new Set<number>();
    for (const b of beams) {
      live.add(b.id);
      this.glideBounce(b, dt);
      const isLocal = local !== null && b.o === local.pid;
      const target = isLocal ? local.aim : b.ang;
      let ang = this.angles.get(b.id);
      if (ang === undefined) {
        ang = isLocal ? local.aim : b.ang;
      } else {
        const rate = BEAM_TURN_RATE * DEG * (isLocal ? local.turnMul : 2);
        ang = turnToward(ang, target, rate * dt);
        if (isLocal && Math.abs(angleDiff(ang, b.ang)) > DISAGREE_RAD) ang = turnToward(ang, b.ang, rate * 2 * dt);
      }
      this.angles.set(b.id, ang);
      const owner = players.find((p) => p.id === b.o);
      const base: Vec2 = owner ? { x: owner.rx, y: owner.ry } : { x: b.x - fromAngle(b.ang, BEAM.offset).x, y: b.y - fromAngle(b.ang, BEAM.offset).y };
      const off = fromAngle(ang, BEAM.offset);
      b.facing = ang;
      b.rx = base.x + off.x;
      b.ry = base.y + off.y;
    }
    for (const id of this.angles.keys()) if (!live.has(id)) this.angles.delete(id);
    for (const id of this.bounces.keys()) if (!live.has(id)) this.bounces.delete(id);
  }

  /** A crossing moves as both beams sweep; ease the drawn crossing point, angle and length toward the server's. */
  private glideBounce(b: ViewBeam, dt: number): void {
    const target = b.bo;
    if (!target) {
      this.bounces.delete(b.id);
      return;
    }
    const prev = this.bounces.get(b.id);
    const k = prev ? 1 - Math.exp(-BOUNCE_FOLLOW * dt) : 1;
    const next: BeamBounce = prev
      ? {
        x: prev.x + (target.x - prev.x) * k,
        y: prev.y + (target.y - prev.y) * k,
        ang: wrapAngle(prev.ang + angleDiff(prev.ang, target.ang) * k),
        len: prev.len + (target.len - prev.len) * k,
        hit: target.hit,
        mul: target.mul,
      }
      : { ...target };
    this.bounces.set(b.id, next);
    b.bo = next;
  }

  clear(): void {
    this.angles.clear();
    this.bounces.clear();
  }
}

/** Turn multiplier for the local beam from the player's statuses (DESIGN 2.7 hasted / chilled). */
export function beamTurnMul(statusIds: readonly string[]): number {
  let m = 1;
  if (statusIds.includes('hasted')) m *= STATUS_EFFECT.hasteBeamTurnMul;
  if (statusIds.includes('chilled')) m *= STATUS_EFFECT.chillBeamTurnMul;
  return m;
}

function turnToward(from: number, to: number, maxStep: number): number {
  const d = angleDiff(from, to);
  if (Math.abs(d) <= maxStep) return wrapAngle(to);
  return wrapAngle(from + Math.sign(d) * maxStep);
}
